import { Button, Select } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import Modal from '../../foundation/components/modal/Modal';
import { useFileDownload } from '../../foundation/utils/fileDownload';
import {
  useCreateReportForSampleMutation,
  usePrepareMolgenReportMutation,
} from '../api/variantApi';
import type { VariantReportData } from '../probands_samples/types';
import type { SampleDetail } from './types';

type ClinicalReportPanelOption = {
  id: number;
  name: string;
};

type SampleDetailClinicalReportModalProps = {
  onClose: () => void;
  onReportGenerated?: () => void;
  open: boolean;
  sample?: SampleDetail;
};

const getUniquePanelOptions = (
  reportData: VariantReportData[],
): ClinicalReportPanelOption[] => {
  const panelOptionsById = new Map<number, ClinicalReportPanelOption>();

  reportData.forEach((panel) => {
    if (!panelOptionsById.has(panel.medicalinvestigation_id)) {
      panelOptionsById.set(panel.medicalinvestigation_id, {
        id: panel.medicalinvestigation_id,
        name: panel.medicalinvestigation_name,
      });
    }
  });

  return Array.from(panelOptionsById.values());
};

const SampleDetailClinicalReportModal = ({
  onClose,
  onReportGenerated,
  open,
  sample,
}: SampleDetailClinicalReportModalProps) => {
  const { downloadFile } = useFileDownload();
  const [prepareMolgenReport] = usePrepareMolgenReportMutation();
  const [createReportForSample] = useCreateReportForSampleMutation();
  const [selectedPanelId, setSelectedPanelId] = useState<number>();
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const panelOptions = useMemo(() => {
    const selectedPanels =
      sample?.genotype_variants.flatMap(
        (variant) => variant.report_data ?? [],
      ) ?? [];

    return getUniquePanelOptions(selectedPanels);
  }, [sample?.genotype_variants]);

  useEffect(() => {
    if (!open) {
      setSelectedPanelId(undefined);
    }
  }, [open]);

  const handleGenerateReport = async () => {
    if (!sample || selectedPanelId == null) {
      return;
    }

    setIsGeneratingReport(true);
    try {
      await prepareMolgenReport({
        sample_id: sample.id,
        medicalinvestigation_id: selectedPanelId,
      }).unwrap();

      const blob = await createReportForSample({
        type: 'molgen',
        sample_id: sample.id,
        medicalinvestigation_id: selectedPanelId,
      }).unwrap();

      downloadFile({
        filename: `molgen_report_${sample.sample_id}_mi${selectedPanelId}.docx`,
        blob,
      });
      onReportGenerated?.();
      onClose();
    } catch {
      // Existing report generation flow handles user-facing errors.
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Please select a panel for the clinical report"
      onCancel={onClose}
      footer={null}
      width={560}
      className="sample-detail-clinical-report-modal"
    >
      <div className="sample-detail-clinical-report-modal__field">
        <div className="sample-detail-clinical-report-modal__label">Panel:</div>
        <Select
          className="sample-detail-clinical-report-modal__select"
          options={panelOptions.map((panel) => ({
            label: panel.name,
            value: panel.id,
          }))}
          placeholder="Select panel"
          value={selectedPanelId}
          onChange={(value: number) => setSelectedPanelId(value)}
          disabled={panelOptions.length === 0}
        />
      </div>

      <div className="sample-detail-clinical-report-modal__actions">
        <Button
          type="primary"
          loading={isGeneratingReport}
          disabled={!sample || selectedPanelId == null || isGeneratingReport}
          onClick={handleGenerateReport}
        >
          Generate report
        </Button>
      </div>
    </Modal>
  );
};

export default SampleDetailClinicalReportModal;
